import { DATE_FORMAT } from "../../modules/vscDashboard/constants";
import {
  SCREEN_STATUS,
  SYNC_FILE,
  SYNC_JOB,
  SYNC_JOB_FILE_STATUS,
  SYNC_JOB_STATUS,
} from "./AntdTable.constants";
import {
  IClause,
  IClauseMeta,
  ICSSStatusObj,
  ISyncDetailsScreenStatus,
} from "./AntdTable.types";
import moment from "moment-timezone";

import emptyBoxSvg from "../../assets/images/ecommerce.svg";
import erroredSvg from "../../assets/no_sync_details.svg";
import syncInProgressSvg from "../../assets/syncing_contracts.svg";

export function isOverflowActive(event: any) {
  return (
    event.offsetHeight < event.scrollHeight ||
    event.offsetWidth < event.scrollWidth
  );
}

export const getFiltersForKCC = (
  filters: {
    [key: string]: [string | boolean];
  },
  clausesForVertical: any
) => {
  const filterKeys = Object.keys(filters).filter((key) => filters?.[key]);
  const noFilters = filterKeys.length === 0;
  if (noFilters) return [];

  const filtersForRequestBody = filterKeys.map((key) => {
    return {
      // TODO: NEED to make it generic: 3 is number of fixed columns
      column_name: clausesForVertical?.[Number(key) - 3]?.name,
      is_present: filters?.[key]?.[0],
    };
  });

  return filtersForRequestBody;
};

export const findClause = (type: string, clauses: any) => {
  return clauses?.findIndex((clause: any) => clause?.type === type);
};

export const getClausesDataFromListApi = (clause_check_details: IClause[]) => {
  const refactoredData: any = {};
  clause_check_details.forEach((clause) => {
    refactoredData[clause.category] = clause;
  });
  return refactoredData;
};

export const getClausesOverviewDataFromListApi = (data: IClauseMeta[]) => {
  const refactoredData: any = {};
  data.forEach((clause) => {
    refactoredData[clause?.metadata_name] = clause;
  });
  return refactoredData;
};

export const getClauseMetadata = (data: IClauseMeta[]) => {
  const hideFields = [
    "contract_type",
    "title",
    "vendor_name",
    "start_date",
    "end_date",
    "maximum_total_cost",
    "maximum_billable_hours",
    "vertical",
    "service_type",
  ];
  const refactoredData =
    data?.filter(
      (clause) =>
        !hideFields?.includes(clause?.metadata_name) && clause?.value !== "N/A"
    ) || [];
  return refactoredData;
};

export const getLocalDateTimeFromTimestamp = (data: {
  timestamp: string;
  dateformat?: string;
  timeformat?: string;
}) => {
  const {
    timestamp = "",
    dateformat = DATE_FORMAT,
    timeformat = "HH:mm A",
  } = data;
  const localTimezone = moment.tz.guess();
  let localDate: moment.Moment;

   // Try parsing as a number (UNIX timestamp with or without milliseconds)
  if (typeof timestamp === "number" || 
      (typeof timestamp === "string" && /^\d+(\.\d+)?$/.test(timestamp))) {
    // Parse as number
    let ms = Number(timestamp);
    if (!isNaN(ms)) {
      if (ms < 9999999999) {
        // seconds, convert to ms
        ms = ms * 1000;
      }
      localDate = moment.tz(ms, localTimezone);
    } else {
      localDate = moment.tz(timestamp, localTimezone); // attempt as ISO date string
    }
  } else  {
    // Try parsing as ISO date string
    localDate = moment.tz(timestamp, localTimezone);
  } 
  return {
    date: localDate.format(dateformat),
    time: localDate.format(timeformat),
  };
};

export const addDaysToCurrentDate = (
  count: number,
  unit: moment.DurationInputArg2 = "days",
  format: string = DATE_FORMAT
) => {
  return moment().add(count, unit).format(format);
};

export const validateEmail = (email: string) => {
  return email.match(
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
};

export const getSyncJobStatusObject = (status: string): ICSSStatusObj => {
  let jobStatusObj: ICSSStatusObj = {
    CSS_CLASS: "",
    LABEL: "",
  };
  switch (status) {
    case SYNC_JOB_STATUS.COMPLETED:
      jobStatusObj = SYNC_JOB.COMPLETED;
      break;
    case SYNC_JOB_STATUS.FAILED:
      jobStatusObj = SYNC_JOB.FAILED;
      break;
    case SYNC_JOB_STATUS.IN_PROGRESS:
    default:
      jobStatusObj = SYNC_JOB.IN_PROGRESS;
      break;
  }
  return jobStatusObj;
};

export const getSyncJobFileObject = (status: string): ICSSStatusObj => {
  let jobStatusObj: ICSSStatusObj = {
    CSS_CLASS: "",
    LABEL: "",
  };
  switch (status) {
    case SYNC_JOB_FILE_STATUS.MOVED:
      jobStatusObj = SYNC_FILE[SYNC_JOB_FILE_STATUS.MOVED];
      break;
    case SYNC_JOB_FILE_STATUS.NOT_MOVED:
      jobStatusObj = SYNC_FILE[SYNC_JOB_FILE_STATUS.NOT_MOVED];
      break;
    case SYNC_JOB_FILE_STATUS.FAILED:
    default:
      jobStatusObj = SYNC_FILE[SYNC_JOB_FILE_STATUS.FAILED];
      break;
  }
  return jobStatusObj;
};

export const getDetailsForSyncScreen = (value: ISyncDetailsScreenStatus) => {
  switch (value) {
    case SCREEN_STATUS.NO_DATA:
      return {
        src: emptyBoxSvg,
        text: "No data to display",
        showComp: false,
      };
    case SCREEN_STATUS.ID_ERROR:
      return {
        src: erroredSvg,
        text: "Invalid Id. No Contract Sync Found with given ID.",
        showComp: true,
      };
    case SCREEN_STATUS.ERROR:
      return {
        src: erroredSvg,
        text: "API Failure. Please try again later.",
        showComp: true,
      };
    case SCREEN_STATUS.LOADING:
      return {
        showComp: false,
        loading: true,
      };
    case SCREEN_STATUS.IN_PROGRESS:
      return {
        src: syncInProgressSvg,
        text: "Syncing Contracts. You can check once all contracts synced.",
        showComp: true,
      };
    default:
      break;
  }
};
